import { IAddress } from 'interfaces/users';

/** Ant Design rules for address fields when `addressEditing` is true. */
export function cepRule(addressEditing: boolean) {
  return () => ({
    validator(_: unknown, value: unknown) {
      if (!addressEditing) {
        return Promise.resolve();
      }

      if (!value || String(value).trim() === '') {
        return Promise.reject(new Error('CEP obrigatório'));
      }

      if (!/^\d{5}-\d{3}$/.test(String(value))) {
        return Promise.reject(new Error('Insira um CEP válido'));
      }

      return Promise.resolve();
    }
  });
}

export function requiredAddressTextRule(addressEditing: boolean, msg: string) {
  return () => ({
    validator(_: unknown, value: unknown) {
      if (!addressEditing) {
        return Promise.resolve();
      }

      if (!value || String(value).trim() === '') {
        return Promise.reject(new Error(msg));
      }

      return Promise.resolve();
    }
  });
}

export function requiredCityRule(addressEditing: boolean) {
  return () => ({
    validator(_: unknown, value: unknown) {
      if (!addressEditing) {
        return Promise.resolve();
      }

      if (!value) {
        return Promise.reject(new Error('Cidade obrigatória'));
      }

      return Promise.resolve();
    }
  });
}

/** Merge form values with existing address so required fields are not omitted from JSON. */
// eslint-disable-next-line sonarjs/cognitive-complexity -- explicit merge rules for API payload
export function buildUserAddressPayload(
  values: Record<string, unknown>,
  existingAddr: IAddress | null | undefined
): Record<string, unknown> {
  const normalizedPostal = values.postal_code
    ? String(values.postal_code).replace(/\D/g, '')
    : undefined;

  const existingPostalDigits = existingAddr?.postal_code
    ? String(existingAddr.postal_code).replace(/\D/g, '')
    : '';

  const pickText = (fromForm: unknown, fallback: string | null | undefined) => {
    if (fromForm != null && String(fromForm).trim() !== '') {
      return String(fromForm);
    }

    return fallback != null && String(fallback).trim() !== ''
      ? String(fallback)
      : '';
  };

  const cityId = values.city_id;
  const resolvedCityId =
    cityId != null && cityId !== ''
      ? String(cityId)
      : existingAddr?.city?.id
      ? String(existingAddr.city.id)
      : '';

  const complementRaw = values.address_complement;
  const address_complement =
    complementRaw !== undefined && complementRaw !== null
      ? String(complementRaw)
      : existingAddr?.address_complement ?? '';

  return {
    ...(existingAddr?.id ? { id: existingAddr.id } : {}),
    postal_code: normalizedPostal || existingPostalDigits,
    street: pickText(values.street, existingAddr?.street),
    address_number: pickText(
      values.address_number,
      existingAddr?.address_number
    ),
    neighborhood: pickText(values.neighborhood, existingAddr?.neighborhood),
    ...(resolvedCityId ? { city_id: resolvedCityId } : {}),
    address_complement
  };
}

export function isAddressPayloadValid(
  payload: Record<string, unknown>
): boolean {
  const cityId = payload.city_id;
  return typeof cityId === 'string' && cityId.length > 0;
}
